import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaSpinner, FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';

function EditInsuranceModal({
  isOpen,
  onClose,
  onSave,
  title,
  type,
  data,
  subbieId,
  isSubbie = false
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    provider: '',
    policy_number: '',
    expiry: '',
    cover_amount: ''
  });

  useEffect(() => {
    if (data) {
      setFormData({
        provider: data.provider || '',
        policy_number: data.policy_number || '',
        expiry: data.expiry || '',
        cover_amount: data.cover_amount || ''
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (type !== 'tax' && !formData.provider) {
        toast.error('Please enter the insurance provider');
        setLoading(false);
        return;
      }
      if (type !== 'tax' && !formData.policy_number) {
        toast.error('Please enter the policy number');
        setLoading(false);
        return;
      }
      if (!formData.expiry) {
        toast.error('Please select the expiry date');
        setLoading(false);
        return;
      }

      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving insurance:', error);
      toast.error('Failed to save insurance details');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getTypeLabel = () => {
    const labels = {
      'git': 'GIT Insurance (Goods in Transit)',
      'pl': 'Public Liability Insurance',
      'tax': 'Tax Clearance Certificate'
    };
    return labels[type] || title || 'Insurance';
  };

  const getTypeIcon = () => {
    const icons = {
      'git': '🚛',
      'pl': '🏢',
      'tax': '📄'
    };
    return icons[type] || '🛡️';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-navy flex items-center gap-2">
            <span>{getTypeIcon()}</span>
            Edit {getTypeLabel()}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-navy transition-colors">
            <FaTimes size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Insurance Provider <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="provider"
                value={formData.provider}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green focus:ring-2 focus:ring-green/10"
                placeholder="e.g. Santam, Old Mutual"
                required={type !== 'tax'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Policy Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="policy_number"
                value={formData.policy_number}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green focus:ring-2 focus:ring-green/10"
                placeholder="e.g. GIT-2026-001"
                required={type !== 'tax'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cover Amount (R)
              </label>
              <input
                type="number"
                name="cover_amount"
                value={formData.cover_amount}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green focus:ring-2 focus:ring-green/10"
                placeholder="e.g. 2000000"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="expiry"
                value={formData.expiry}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green focus:ring-2 focus:ring-green/10"
                required
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700 flex items-start gap-2">
                <FaInfoCircle className="mt-0.5 flex-shrink-0" />
                <span>
                  {isSubbie ? (
                    'Your changes will be sent for admin approval. You will be notified once approved.'
                  ) : (
                    'Changes will be saved immediately and logged in the audit trail.'
                  )}
                </span>
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave size={14} />}
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditInsuranceModal;
